import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { activityVerb } from "@/lib/activityLabels";
import { queryKeys } from "@/lib/queryKeys";
import type { ActivityRow } from "@shared/types";
import { Skeleton } from "@/components/Skeleton";
import { Drawer } from "@/components/Drawer";
import { Icon } from "@/components/Icon";

export function InboxBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const unread = useQuery({
    queryKey: queryKeys.inboxUnread(),
    queryFn: api.inboxUnread,
    refetchInterval: 30_000,
  });
  const items = useQuery({
    queryKey: queryKeys.inbox(),
    queryFn: api.inbox,
    enabled: open,
  });
  const markRead = useMutation({
    mutationFn: (activityId: string) => api.inboxMarkRead(activityId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.inbox() });
      qc.invalidateQueries({ queryKey: queryKeys.inboxUnread() });
    },
  });
  const markAll = useMutation({
    mutationFn: api.inboxMarkAllRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.inbox() });
      qc.invalidateQueries({ queryKey: queryKeys.inboxUnread() });
    },
  });

  const unreadCount = unread.data?.unread ?? 0;

  return (
    <>
      <button
        type="button"
        className="tb-iconbtn inbox-bell"
        aria-label={`Inbox (${unreadCount} unread)`}
        onClick={() => setOpen(true)}
      >
        <Icon name="bell" />
        {unreadCount > 0 && <span className="inbox-badge">{unreadCount}</span>}
      </button>
      <Drawer open={open} onClose={() => setOpen(false)} title="Notifications">
        {items.isLoading && <Skeleton width={120} height={16} />}
        {items.data?.items.length === 0 && <p>Nothing new.</p>}
        {unreadCount > 0 && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            style={{ marginBottom: 10 }}
            disabled={markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            {markAll.isPending ? "Marking…" : `Mark all ${unreadCount} read`}
          </button>
        )}
        {/* Skip project-level activity (task_id null) — the inbox link points at a task. */}
        {items.data?.items
          .filter(
            (a: ActivityRow): a is ActivityRow & { task_id: string } =>
              a.task_id !== null,
          )
          .map((a) => (
            <Link
              key={a.id}
              to="/tasks/$taskId"
              params={{ taskId: a.task_id }}
              className="nd-item"
              onClick={() => {
                markRead.mutate(a.id);
                setOpen(false);
              }}
            >
              <div className="t-sm">{activityVerb(a.action)}</div>
              <div
                style={{
                  color: "var(--color-fg-muted)",
                  fontSize: 11,
                }}
              >
                {new Date(a.created_at).toLocaleString()}
              </div>
            </Link>
          ))}
      </Drawer>
    </>
  );
}
